import { useEffect, useRef, useState } from 'react';
import { gameSessionService } from '../../services/api/gameSessionService';
import './KitchenHud.css';

/**
 * Kitchen Rush HUD (GameKind "kitchen") — the ticket rail + the ACTION button.
 *
 * The server owns tickets/score/clock (KitchenRules); Unity owns your hands
 * (FlexoKitchenController streams {prompt,carry,canAct} via the 'kitchenStatus' bridge
 * event; the ACTION button = one E press via sendUnityMessage('FlexoKitchen','OnAction')).
 * Tickets expire server-side but lazily — this HUD pokes 'sync' when the earliest ticket's
 * deadline passes (the generic GamePanel poke only covers the round clock).
 */

interface KitchenTicket {
  id: number;
  dish: string;
  deadline: string;
}

interface KitchenServerState {
  tickets?: KitchenTicket[];
  score?: number;
  target?: number;
  missed?: number;
}

interface KitchenStatus {
  prompt: string;
  carry: string;
  canAct: boolean;
}

type SendUnityMessage = (go: string, method: string, param?: string | number) => void;

const DISH_ICON: Record<string, string> = { salad: '🥗', soup: '🍜', burger: '🍔' };

export function KitchenHud({
  roomId,
  gameId,
  state,
}: {
  roomId: string;
  gameId: string;
  state: KitchenServerState;
}) {
  const [status, setStatus] = useState<KitchenStatus | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const pokedTicketRef = useRef<number>(-1);

  useEffect(() => {
    const onUnityEvent = (e: Event) => {
      const detail = (e as CustomEvent<{ eventName: string; data: unknown }>).detail;
      if (detail?.eventName !== 'kitchenStatus') return;
      try {
        const s = typeof detail.data === 'string' ? JSON.parse(detail.data) : detail.data;
        setStatus(s as KitchenStatus);
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('unity-event', onUnityEvent);
    return () => window.removeEventListener('unity-event', onUnityEvent);
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(id);
  }, []);

  // Per-ticket expiry poke: one 'sync' per expired ticket id.
  useEffect(() => {
    const tickets = state.tickets ?? [];
    for (const t of tickets) {
      const dl = Date.parse(t.deadline);
      if (dl && now >= dl && t.id > pokedTicketRef.current) {
        pokedTicketRef.current = t.id;
        void gameSessionService.sendDelta(roomId, gameId, 'sync');
        break;
      }
    }
  }, [now, state.tickets, roomId, gameId]);

  const act = () => {
    const send = (window as Window & { sendUnityMessage?: SendUnityMessage }).sendUnityMessage;
    send?.('FlexoKitchen', 'OnAction', '{}');
  };

  const tickets = state.tickets ?? [];

  return (
    <div className="kitchen-hud">
      <div className="kitchen-tickets">
        {tickets.map((t) => {
          const remain = Math.max(0, Math.ceil((Date.parse(t.deadline) - now) / 1000));
          const urgent = remain <= 15;
          return (
            <div key={t.id} className={`kitchen-ticket${urgent ? ' urgent' : ''}`}>
              <span className="kitchen-dish">{DISH_ICON[t.dish] ?? '🍽️'}</span>
              <span className="kitchen-dish-name">{t.dish}</span>
              <span className="kitchen-ticket-clock">{remain}s</span>
            </div>
          );
        })}
        <div className="kitchen-score">
          {state.score ?? 0}/{state.target ?? 0} served
          {(state.missed ?? 0) > 0 && <span className="kitchen-missed"> · {state.missed} missed</span>}
        </div>
      </div>

      <div className="kitchen-controls">
        {status?.carry && <span className="kitchen-carry">👐 {status.carry}</span>}
        <button type="button" className="kitchen-action" disabled={!status?.canAct} onPointerDown={act}>
          {status?.prompt || 'Walk to a station'}
        </button>
        <p className="kitchen-hint">Desktop: press E at a station.</p>
      </div>
    </div>
  );
}
